import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { User, ViewName, NavEntry } from "../types";

interface AppState {
  currentView: ViewName;
  activeId: string | null;
  activeUser: User | null;
  allUsers: User[];
  navHistory: NavEntry[];
  navIndex: number;
  panelOpen: boolean;
  panelExpanded: boolean;
  treeViewActive: boolean;
  activeProject: string;
  createDriverRequirementId: string | null;
  createTestRequirementId: string | null;
  impersonatedRole: string | null;
  authRole: string;
  authUserId: string;
}

interface AppContextValue extends AppState {
  setCurrentView: (view: ViewName) => void;
  setActiveId: (id: string | null) => void;
  setActiveUser: (user: User | null) => void;
  setAllUsers: (users: User[]) => void;
  navigateTo: (view: ViewName, id?: string | null) => void;
  navigateToCreate: (driverRequirementId?: string | null) => void;
  navigateToCreateTest: (requirementId?: string | null) => void;
  navBack: () => void;
  navForward: () => void;
  setPanelOpen: (open: boolean) => void;
  setPanelExpanded: (expanded: boolean) => void;
  setTreeViewActive: (active: boolean) => void;
  setActiveProject: (project: string) => void;
  setImpersonatedRole: (role: string | null) => void;
  setAuthRole: (role: string) => void;
  setAuthUserId: (id: string) => void;
  effectiveRole: () => string;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>({
    currentView: "requirements",
    activeId: null,
    activeUser: null,
    allUsers: [],
    navHistory: [],
    navIndex: -1,
    panelOpen: false,
    panelExpanded: false,
    treeViewActive: false,
    activeProject: "",
    createDriverRequirementId: null,
    createTestRequirementId: null,
    impersonatedRole: null,
    authRole: "",
    authUserId: "",
  });

  const setCurrentView = useCallback((view: ViewName) => {
    setState(s => ({ ...s, currentView: view, activeId: null, panelOpen: false, panelExpanded: false }));
  }, []);

  const setActiveId = useCallback((id: string | null) => {
    setState(s => ({ ...s, activeId: id }));
  }, []);

  const setActiveUser = useCallback((user: User | null) => {
    setState(s => ({ ...s, activeUser: user }));
  }, []);

  const setAllUsers = useCallback((users: User[]) => {
    setState(s => ({ ...s, allUsers: users }));
  }, []);

  const navigateTo = useCallback((view: ViewName, id: string | null = null) => {
    setState(s => {
      const newHistory = [...s.navHistory.slice(0, s.navIndex + 1), { view, id }];
      return {
        ...s,
        currentView: view,
        activeId: id,
        navHistory: newHistory,
        navIndex: newHistory.length - 1,
        panelOpen: id !== null,
      };
    });
  }, []);

  const navigateToCreate = useCallback((driverRequirementId?: string | null) => {
    setState(s => ({
      ...s,
      currentView: "create-requirement" as ViewName,
      activeId: null,
      panelOpen: false,
      panelExpanded: false,
      createDriverRequirementId: driverRequirementId ?? null,
    }));
  }, []);

  const navigateToCreateTest = useCallback((requirementId?: string | null) => {
    setState(s => ({
      ...s,
      currentView: "create-test" as ViewName,
      activeId: null,
      panelOpen: false,
      panelExpanded: false,
      createTestRequirementId: requirementId ?? null,
    }));
  }, []);

  const navBack = useCallback(() => {
    setState(s => {
      if (s.navIndex <= 0) return s;
      const newIndex = s.navIndex - 1;
      const entry = s.navHistory[newIndex];
      return { ...s, navIndex: newIndex, currentView: entry.view, activeId: entry.id, panelOpen: entry.id !== null };
    });
  }, []);

  const navForward = useCallback(() => {
    setState(s => {
      if (s.navIndex >= s.navHistory.length - 1) return s;
      const newIndex = s.navIndex + 1;
      const entry = s.navHistory[newIndex];
      return { ...s, navIndex: newIndex, currentView: entry.view, activeId: entry.id, panelOpen: entry.id !== null };
    });
  }, []);

  const setPanelOpen = useCallback((open: boolean) => {
    setState(s => ({ ...s, panelOpen: open, panelExpanded: open ? s.panelExpanded : false }));
  }, []);

  const setPanelExpanded = useCallback((expanded: boolean) => {
    setState(s => ({ ...s, panelExpanded: expanded }));
  }, []);

  const setTreeViewActive = useCallback((active: boolean) => {
    setState(s => ({ ...s, treeViewActive: active }));
  }, []);

  const setActiveProject = useCallback((project: string) => {
    setState(s => ({ ...s, activeProject: project }));
  }, []);

  const setImpersonatedRole = useCallback((role: string | null) => {
    setState(s => ({ ...s, impersonatedRole: role }));
  }, []);

  const setAuthRole = useCallback((role: string) => {
    setState(s => ({ ...s, authRole: role }));
  }, []);

  const setAuthUserId = useCallback((id: string) => {
    setState(s => ({ ...s, authUserId: id }));
  }, []);

  const effectiveRole = useCallback(() => {
    if (state.impersonatedRole) return state.impersonatedRole;
    return state.activeUser?.role ?? "viewer";
  }, [state.impersonatedRole, state.activeUser]);

  // Build effective activeUser with impersonated role applied
  const effectiveActiveUser = state.activeUser && state.impersonatedRole
    ? { ...state.activeUser, role: state.impersonatedRole as User["role"] }
    : state.activeUser;

  const value: AppContextValue = {
    ...state,
    activeUser: effectiveActiveUser,
    setCurrentView,
    setActiveId,
    setActiveUser,
    setAllUsers,
    navigateTo,
    navigateToCreate,
    navigateToCreateTest,
    navBack,
    navForward,
    setPanelOpen,
    setPanelExpanded,
    setTreeViewActive,
    setActiveProject,
    setImpersonatedRole,
    setAuthRole,
    setAuthUserId,
    effectiveRole,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
